import type React from 'react';
import { useCallback, useId, useState } from 'react';

/** The attributes that tie the control that opens a popover to the popover. */
export interface PopoverTriggerAria {
  'aria-haspopup': 'dialog';
  'aria-expanded': boolean;
  /** The popup's id while it is open. A closed popover is not in the document. */
  'aria-controls': string | undefined;
}

/** Spread onto the control that opens the popover. */
export interface PopoverTriggerProps extends PopoverTriggerAria {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

/**
 * The trigger attributes for a popover whose open state the caller keeps itself, such as a class
 * component, which cannot call `usePopoverState`. `id` is the id passed to the popover.
 */
export const getPopoverTriggerAria = (isOpen: boolean, id: string): PopoverTriggerAria => ({
  'aria-haspopup': 'dialog',
  'aria-expanded': isOpen,
  'aria-controls': isOpen ? id : undefined,
});

/** Spread onto the Popover, next to its own `aria-label` and placement props. */
export interface PopoverStateProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  id: string;
}

export interface UsePopoverStateResult {
  triggerProps: PopoverTriggerProps;
  popoverProps: PopoverStateProps;
  isOpen: boolean;
  /**
   * Opens the popover against the element whose handler receives the event, or against `anchorRef`
   * when it is set, for a trigger with its own click handler. Spread `triggerProps` on that trigger first and put its own `onClick` after
   * the spread, so it keeps the ARIA attributes. Call it during the handler, before any `await`:
   * React clears `currentTarget` once the handler returns.
   */
  openFrom: (event: React.SyntheticEvent<HTMLElement>) => void;
  /** Closes the popover, for content that closes it after an action. */
  close: () => void;
}

export interface UsePopoverStateOptions {
  /**
   * The element the popover opens against, instead of the clicked trigger. An inline popover
   * (`layer="inline"`) needs it: it mounts inside its anchor, so the anchor is a positioned wrapper
   * around the trigger, not the trigger itself.
   */
  anchorRef?: React.RefObject<HTMLElement>;
}

/**
 * The open state of one Popover and the props that connect it to its trigger: the clicked element
 * becomes the anchor, unless `anchorRef` names another one, and the trigger announces the popup
 * with aria-haspopup, aria-expanded and aria-controls. The trigger and the popover can live in
 * different components: pass each half where it is rendered.
 */
const usePopoverState = ({ anchorRef }: UsePopoverStateOptions = {}): UsePopoverStateResult => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const id = useId();
  const isOpen = anchorEl !== null;

  // Stable across renders, so a caller can list them in a dependency array or pass them to a
  // memoized component without checking how this hook builds them.
  const openFrom = useCallback(
    (event: React.SyntheticEvent<HTMLElement>) => setAnchorEl(anchorRef?.current ?? event.currentTarget),
    [anchorRef]
  );
  const close = useCallback(() => setAnchorEl(null), []);

  return {
    triggerProps: { onClick: openFrom, ...getPopoverTriggerAria(isOpen, id) },
    popoverProps: { open: isOpen, anchorEl, onClose: close, id },
    isOpen,
    openFrom,
    close,
  };
};

export default usePopoverState;
